import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  IconButton,
  Button,
  Tooltip,
  Typography,
  Box,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Menu } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  LogoutIcon,
  AvatarIcon,
  ExpandMoreIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  ModalConfirm,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import AzureSsoUserPickerModal from '../../containers/Login/AzureSsoUserPickerModal';
import messages from './messages';
import appMessages from '../../containers/App/messages';

const UserMenu = ({
  userMenuAnchorRef,
  handleUserMenuToggle,
  userMenuOpen,
  handleUserMenuClose,
  logout,
  fullName,
  isXs,
  availableUsers = [],
  currentUserId,
  azureIdToken,
  showUserPicker = false,
  onSwitchUser,
  onSelectUser,
  onHideUserPicker,
}) => {
  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();
  const [showSwitchUserModal, setShowSwitchUserModal] = useState(false);

  const [firstName, lastName] = fullName?.split(' ') ?? [];

  // Show Switch User option if there are multiple available users
  const canSwitchUser = availableUsers && availableUsers.length > 1;

  const handleSwitchUser = () => {
    handleUserMenuClose();
    // If the Azure token wasn't persisted (e.g. after a page refresh), delegate to
    // the switch-user saga which will trigger a fresh popup to re-acquire it.
    // If we have a live token, open the cached user list directly.
    if (!azureIdToken) {
      if (onSwitchUser) onSwitchUser();
    } else {
      setShowSwitchUserModal(true);
    }
  };

  const handleCloseSwitchUserModal = () => {
    setShowSwitchUserModal(false);
    if (onHideUserPicker) onHideUserPicker();
  };

  const handleSelectUser = (userId) => {
    setShowSwitchUserModal(false);
    // Dispatch the select user action which will trigger the saga to switch accounts
    if (onSelectUser) {
      onSelectUser(userId);
    }
  };

  // Build menu options dynamically
  const menuOptions = [];

  if (canSwitchUser) {
    menuOptions.push({
      icon: <AvatarIcon />,
      text: <FormattedMessage {...messages.switchUser} />,
      value: 'switchUser',
      onClick: handleSwitchUser,
    });
  }

  menuOptions.push({
    icon: <LogoutIcon />,
    text: <FormattedMessage {...messages.logOut} />,
    value: 'logout',
    onClick: openConfirmModal,
  });

  return (
    <>
      {isXs ? (
        <IconButton
          ref={userMenuAnchorRef}
          onClick={handleUserMenuToggle}
          sx={{ margin: '0 !important', padding: '0 !important' }}
        >
          <AvatarIcon sx={{ color: 'white' }} />
        </IconButton>
      ) : (
        <Button
          ref={userMenuAnchorRef}
          onClick={handleUserMenuToggle}
          startIcon={<AvatarIcon />}
          endIcon={<ExpandMoreIcon />}
          sx={{
            color: 'white',
            mr: 2,
          }}
          variant={'text'}
        >
          <Tooltip title={fullName}>
            <Box>
              <Typography
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 150,
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  color: 'white',
                }}
              >
                <span id={'user_first-name'} style={{ marginRight: 4 }}>
                  {firstName?.trim()}
                </span>
                <span id={'user_last-name'}>{lastName?.trim()}</span>
              </Typography>
            </Box>
          </Tooltip>
        </Button>
      )}
      <Menu
        id={'userMenu'}
        open={userMenuOpen}
        closeOnClick={true}
        anchorEl={userMenuAnchorRef.current}
        options={menuOptions}
        handleClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      />

      <ModalConfirm
        title={<FormattedMessage {...messages.confirmLogOut} />}
        open={openConfirm}
        onClose={closeConfirmModal}
        actionsProps={[
          {
            children: <FormattedMessage {...appMessages.cancel} />,
            onClick: closeConfirmModal,
            variant: 'text',
          },
          {
            startIcon: <LogoutIcon />,
            children: <FormattedMessage {...messages.logOut} />,
            onClick: () => {
              logout();
              closeConfirmModal();
            },
          },
        ]}
      />

      <AzureSsoUserPickerModal
        open={showSwitchUserModal || showUserPicker}
        users={availableUsers}
        onSelectUser={handleSelectUser}
        onClose={handleCloseSwitchUserModal}
        currentUserId={currentUserId}
      />
    </>
  );
};

export default UserMenu;
